import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Target, CheckCircle, AlertCircle, BookOpen, Wifi } from 'lucide-react';

interface TestWelcomeProps {
  studentName: string;
  onStartTest: () => void;
  testDetails: {
    duration: number;
    totalQuestions: number;
    attempts: number;
    deadline: string;
  };
}

const TestWelcome: React.FC<TestWelcomeProps> = ({ studentName, onStartTest, testDetails }) => {
  return (
    <div className="test-welcome-container">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="welcome-card"
      >
        {/* Header */}
        <div className="welcome-header">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="welcome-icon"
          >
            🎯
          </motion.div>
          <h1 className="welcome-title">¡Bienvenido/a, {studentName}!</h1>
          <p className="welcome-subtitle">Test de Ubicación de Inglés - CHILDREN IBC</p>
        </div>

        {/* Test Information Cards */}
        <div className="test-info-grid">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="info-card"
          >
            <Clock className="info-icon" />
            <div className="info-content">
              <h3>Duración del Test</h3>
              <p><strong>{testDetails.duration} minutos</strong></p>
              <small>Una vez iniciado, el cronómetro seguirá corriendo aunque cierres la página</small>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="info-card"
          >
            <Target className="info-icon" />
            <div className="info-content">
              <h3>Total de Preguntas</h3>
              <p><strong>{testDetails.totalQuestions} preguntas</strong></p>
              <small>Pre - A1 (7) + A1 (7) + A2 (6)</small>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="info-card"
          >
            <CheckCircle className="info-icon" />
            <div className="info-content">
              <h3>Intentos Permitidos</h3>
              <p><strong>{testDetails.attempts} intento</strong></p>
              <small>Una sola oportunidad para completar el test</small>
            </div>
          </motion.div>

          {/* Fecha límite oculta */}
          {/* <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
            className="info-card"
          >
            <AlertCircle className="info-icon" />
            <div className="info-content">
              <h3>Fecha Límite</h3>
              <p><strong>{testDetails.deadline}</strong></p>
              <small>Completa el test antes de esta fecha</small>
            </div>
          </motion.div> */}
        </div>

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="instructions-section"
        >
          <h2 className="instructions-title">📋 Instrucciones Importantes</h2>
          
          <div className="instructions-list">
            <div className="instruction-item">
              <Wifi className="instruction-icon" />
              <span>Revisa que tu conexión a internet sea estable antes de empezar</span>
            </div>
            
            {/* Instrucción de repaso oculta */}
            {/* <div className="instruction-item">
              <BookOpen className="instruction-icon" />
              <span>Se recomienda repasar conceptos básicos de inglés antes de iniciar</span>
            </div> */}
            
            <div className="instruction-item">
              <Clock className="instruction-icon" />
              <span>Una vez iniciado el test, el cronómetro seguirá corriendo (30 minutos)</span>
            </div>
            
            <div className="instruction-item">
              <Target className="instruction-icon" />
              <span>Lee cada pregunta cuidadosamente antes de responder</span>
            </div>
          </div>
        </motion.div>

        {/* Start Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="start-section"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onStartTest}
            className="start-button"
          >
            🚀 INICIAR TEST DE UBICACIÓN 🚀
          </motion.button>
          
          <p className="start-note">
            Al hacer clic en "INICIAR", comenzará el cronómetro y no podrás pausar el test
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default TestWelcome;
